import { useMemo, useState, useCallback } from 'react';

export interface UseSearchFilterOptions<T> {
  /** Data to filter */
  data: T[];
  /** Fields to search within each item */
  searchFields: (keyof T | string)[];
}

export interface UseSearchFilterReturn<T> {
  /** Current search term */
  searchTerm: string;
  /** Set search term */
  setSearchTerm: (term: string) => void;
  /** Clear search */
  clearSearch: () => void;
  /** Filtered data */
  filteredData: T[];
  /** Whether search is active */
  hasSearch: boolean;
}

/**
 * Hook for client-side text search filtering.
 * Searches specified fields for the search term (case-insensitive).
 */
export function useSearchFilter<T extends Record<string, any>>({
  data,
  searchFields,
}: UseSearchFilterOptions<T>): UseSearchFilterReturn<T> {
  const [searchTerm, setSearchTerm] = useState('');

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }

    const searchLower = searchTerm.toLowerCase();
    
    return data.filter((item) => {
      return searchFields.some((field) => {
        // Support nested fields like 'patient.firstName'
        const value = getNestedValue(item, field as string);
        return value?.toString().toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchTerm, searchFields]);

  return {
    searchTerm,
    setSearchTerm,
    clearSearch,
    filteredData,
    hasSearch: searchTerm.trim().length > 0,
  };
}

/** Helper to get nested object values like 'patient.firstName' */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}
